import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../../styling/stylingHelper';
import type { QuantityAuditResult } from '../../../../../services/admin/adminService';
import { AuditScoreReport } from '../../AuditReview';
import { PlatformCopyCell, PromptCopyCell } from '../shared/copyCells';
import { buildQuantityBuckets } from './quantityBuckets';
import type { TFn } from '../types';

export function QuantityAuditView({ result, setResult, t, restaurantName }: { result: QuantityAuditResult; setResult: React.Dispatch<React.SetStateAction<QuantityAuditResult | null>>; t: TFn; restaurantName: string }) {
  const dismissRow = (index: number) => {
    const updated = result.flaggedProducts.filter((_, i) => i !== index);
    setResult({ ...result, flaggedProducts: updated, totalFlagged: updated.length });
  };

  const scanned = result.totalProductsScanned;
  const flagged = result.flaggedProducts.length;
  const scorePct = scanned > 0 ? ((scanned - flagged) / scanned) * 100 : 100;

  const fullTable = (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={cn(secondaryText, 'border-b border-gray-200 dark:border-gray-600')}>
                <th className="py-2 px-2 w-6"></th>
                <th className="text-left py-2 px-2">Platform</th>
                <th className="text-left py-2 px-2">Article ID</th>
                <th className="text-left py-2 px-2">Product Name</th>
                <th className="text-right py-2 px-2">Qty</th>
                <th className="text-right py-2 px-2">Raw Qty</th>
                <th className="text-left py-2 px-2">Quantity Info</th>
                <th className="text-right py-2 px-2">Unit Price</th>
                <th className="text-left py-2 px-2">Reason</th>
                <th className="py-2 px-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {result.flaggedProducts.map((p, i) => (
                <tr
                  key={i}
                  className={cn(
                    'border-b border-gray-100 dark:border-gray-700',
                    i % 2 === 0 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''
                  )}
                >
                  <td className="py-2 px-2 text-center">
                    <PromptCopyCell product={p} />
                  </td>
                  <td className={cn(primaryText, 'py-2 px-2 font-mono')}>
                    <PlatformCopyCell platform={p.platform} gmailId={p.gmailId} />
                  </td>
                  <td className={cn(primaryText, 'py-2 px-2 font-mono')}>{p.articleId || '-'}</td>
                  <td className={cn(primaryText, 'py-2 px-2 max-w-xs truncate')} title={p.productName}>
                    {p.productName}
                  </td>
                  <td className={cn(primaryText, 'py-2 px-2 font-mono text-right')}>
                    {p.quantity % 1 === 0 ? p.quantity : p.quantity.toFixed(3)}
                  </td>
                  <td className={cn(primaryText, 'py-2 px-2 font-mono text-right')}>
                    {p.rawQuantity}
                  </td>
                  <td className={cn(secondaryText, 'py-2 px-2 font-mono whitespace-nowrap')}>
                    {p.quantityInfo ? (
                      <div>
                        <span>{p.quantityInfo.stdMultiplier} x {p.quantityInfo.stdAmount}{p.quantityInfo.stdUnit}</span>
                        {p.quantityInfo.weightString && (
                          <div className="text-gray-400 dark:text-textQuaternary-dark text-[10px] truncate max-w-[150px]" title={p.quantityInfo.weightString}>
                            ws: {p.quantityInfo.weightString}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-red-500 dark:text-textRed-dark">null</span>
                    )}
                  </td>
                  <td className={cn(primaryText, 'py-2 px-2 font-mono text-right')}>
                    €{p.unitPrice.toFixed(2)}
                  </td>
                  <td className={cn('py-2 px-2 text-orange-600 dark:text-orange-400')}>
                    {p.reason}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      onClick={() => dismissRow(i)}
                      className="text-gray-400 dark:text-textQuaternary-dark hover:text-red-500 dark:hover:text-textRed-dark transition-colors"
                      title="Dismiss"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
  );

  return (
    <AuditScoreReport
      t={t}
      title={t('adminTesting.review.scoreTitleQty', { restaurant: restaurantName || '—' })}
      scorePct={scorePct}
      scannedLabel={t('adminTesting.review.scannedProducts', { count: scanned })}
      goodLabel={t('adminTesting.review.goodQty', { count: scanned - flagged })}
      buckets={buildQuantityBuckets(result.flaggedProducts, t)}
      costLabel={t('adminTesting.aiCost', { cost: result.aiCost.totalCostEur.toFixed(4), tokens: result.aiCost.inputTokens + result.aiCost.outputTokens })}
      fullBreakdown={flagged > 0 ? fullTable : undefined}
    />
  );
}
